export interface ObjectMap {
  [key: string]: string;
}
interface SettingValueMap {
  value: string;
}
interface SettingArrValueMap {
  value: Array<String>;
}
interface DefaultMap {
  target: string | Element;
  choices: Array<string>;
  type: string;
}

interface DefaultVStringMap extends DefaultMap, SettingValueMap {}

interface DefaultVArrayMap extends DefaultMap, SettingArrValueMap {}

export interface SettingState {
  saveLocal: string;
  storeKey: string;
  setting: {
    app_name: SettingValueMap;
    theme_scheme: SettingValueMap;
    theme_scheme_direction: SettingValueMap;
    theme_style_appearance: SettingArrValueMap;
    theme_color: {
      colors: ObjectMap;
      value: string;
    };
    theme_transition: SettingValueMap;
    theme_font_size: SettingValueMap;
    page_layout: SettingValueMap;
    header_navbar: SettingValueMap;
    header_banner: SettingValueMap;
    sidebar_color: SettingValueMap;
    sidebar_type: SettingArrValueMap;
    sidebar_show: SettingArrValueMap;
    navbar_show: SettingArrValueMap;
    sidebar_menu_style: SettingValueMap;
    footer: SettingValueMap;
    body_font_family: SettingValueMap;
    heading_font_family: SettingValueMap;
    card_style: SettingValueMap;
  };
}

export interface SettingDefaultState {
  saveLocal: string;
  storeKey: string;
  setting: {
    app_name: DefaultVStringMap;
    theme_scheme: DefaultVStringMap;
    theme_scheme_direction: DefaultVStringMap;
    theme_style_appearance: DefaultVArrayMap;
    theme_color: {
      target: string | Element;
      choices: Array<string>;
      type: string;
      colors: ObjectMap;
      value: string;
    };
    theme_transition: DefaultVStringMap;
    theme_font_size: DefaultVStringMap;
    page_layout: DefaultVStringMap;
    header_navbar: DefaultVStringMap;
    header_banner: DefaultVStringMap;
    sidebar_color: DefaultVStringMap;
    sidebar_type: DefaultVArrayMap;
    sidebar_show: DefaultVArrayMap;
    navbar_show: DefaultVArrayMap;
    sidebar_menu_style: DefaultVStringMap;
    footer: DefaultVStringMap;
    body_font_family: DefaultVStringMap;
    heading_font_family: DefaultVStringMap;
    card_style: DefaultVStringMap;
  };
}

export const initialState: SettingState = {
  saveLocal: 'sessionStorage',
  storeKey: 'huisetting-angular',
  setting: {
    app_name: {
      value: 'Hope UI',
    },
    theme_scheme_direction: {
      value: 'ltr',
    },
    theme_scheme: {
      value: 'light',
    },
    theme_style_appearance: {
      value: ['theme-default'],
    },
    theme_color: {
      colors: {},
      value: 'theme-color-default',
    },
    theme_transition: {
      value: 'theme-with-animation',
    },
    theme_font_size: {
      value: 'theme-fs-md',
    },
    page_layout: {
      value: 'container-fluid',
    },
    header_navbar: {
      value: 'default',
    },
    header_banner: {
      value: 'default',
    },
    sidebar_color: {
      value: 'sidebar-white',
    },
    sidebar_type: {
      value: [],
    },
    sidebar_show: {
      value: [],
    },
    navbar_show: {
      value: [],
    },
    sidebar_menu_style: {
      value: 'left-bordered',
    },
    footer: {
      value: 'default',
    },
    body_font_family: {
      value: 'Inter',
    },
    heading_font_family: {
      value: 'Inter',
    },
    card_style: {
      value: 'card-default',
    },
  },
};

// Default Setting State
export const defaultState: SettingDefaultState = {
  saveLocal: 'sessionStorage',
  storeKey: 'huisetting',
  setting: {
    app_name: {
      target: '[data-setting="app_name"]',
      choices: [],
      type: 'text',
      value: 'Hope UI',
    },
    theme_scheme_direction: {
      target: 'html',
      choices: ['ltr', 'rtl'],
      type: 'layout_design',
      value: 'ltr',
    },
    theme_scheme: {
      target: 'html',
      choices: ['light', 'dark', 'auto'],
      type: 'layout_design',
      value: 'light',
    },
    theme_style_appearance: {
      target: 'body',
      choices: ['theme-default', 'theme-flat', 'theme-bordered', 'theme-sharp'],
      type: 'layout_design',
      value: ['theme-default'],
    },
    theme_color: {
      target: 'html',
      choices: ['color-1', 'color-2', 'color-3', 'color-4', 'color-5'],
      type: 'default',
      colors: {},
      value: 'theme-color-default',
    },
    card_style: {
      target: 'body',
      choices: ['card-default', 'card-glass', 'card-transparent'],
      type: 'layout_design',
      value: 'card-default',
    },
    theme_transition: {
      target: 'body',
      choices: ['theme-without-animation', 'theme-with-animation'],
      type: 'layout_design',
      value: 'theme-with-animation',
    },
    theme_font_size: {
      target: 'html',
      choices: ['theme-fs-sm', 'theme-fs-md', 'theme-fs-lg'],
      type: 'layout_design',
      value: 'theme-fs-md',
    },
    page_layout: {
      target: '#page_layout',
      choices: ['container', 'container-fluid'],
      type: 'layout_design',
      value: 'container-fluid',
    },
    header_navbar: {
      target: '.iq-navbar',
      choices: [
        'default',
        'fixed',
        'navs-sticky',
        'nav-glass',
        'navs-transparent',
        'boxed',
        'hidden',
      ],
      type: 'layout_design',
      value: 'default',
    },
    header_banner: {
      target: '.iq-banner',
      choices: ['default', 'navs-bg-color', 'hide'],
      type: 'layout_design',
      value: 'default',
    },
    sidebar_color: {
      target: '[data-toggle="main-sidebar"]',
      choices: [
        'sidebar-white',
        'sidebar-dark',
        'sidebar-color',
        'sidebar-transparent',
      ],
      type: 'layout_design',
      value: 'sidebar-white',
    },
    sidebar_type: {
      target: '[data-toggle="main-sidebar"]',
      choices: ['sidebar-hover', 'sidebar-mini', 'sidebar-boxed'],
      type: 'layout_design',
      value: [],
    },
    sidebar_show: {
      target: '[data-toggle="main-sidebar"]',
      choices: ['sidebar-none'],
      type: 'layout_design',
      value: [],
    },
    navbar_show: {
      target: '[data-toggle="main-sidebar"]',
      choices: ['sidebar-none'],
      type: 'layout_design',
      value: [],
    },
    sidebar_menu_style: {
      target: '[data-toggle="main-sidebar"]',
      choices: [
        'sidebar-default navs-rounded',
        'sidebar-default navs-rounded-all',
        'sidebar-default navs-pill',
        'sidebar-default navs-pill-all',
        'left-bordered',
      ],
      type: 'layout_design',
      value: 'left-bordered',
    },
    footer: {
      target: '.footer',
      choices: ['default', 'sticky', 'glass'],
      type: 'layout_design',
      value: 'default',
    },
    body_font_family: {
      target: 'body',
      choices: [],
      type: 'variable',
      value: 'Inter',
    },
    heading_font_family: {
      target: 'heading',
      choices: [],
      type: 'variable',
      value: 'Inter',
    },
  },
};
